// Left rail (element 040) and phone bottom tabs + "more" sheet (element 041), plan 8.2.2 · 8.2.4.
// Items come from the effective permissions (menuFor / bottomTabsFor) — never from a role name.
import { useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Link } from 'react-router';
import { usePerms } from '../auth/AuthContext';
import { bottomTabsFor, menuFor, menuItem, type MenuId, type MenuItem } from '../auth/permissions';
import { useT } from '../i18n';
import { cls } from '../lib/util';
import { Icon } from '../ui/Icon';
import { refreshTip } from '../ui/layers';
import { Modal } from '../ui/Modal';

export interface RailProps {
  current: MenuId | null;
  /** desktop: narrow (76 px) rail, remembered in this browser */
  narrow: boolean;
  /** tablet: rail opened over the content */
  open: boolean;
  tablet: boolean;
  onToggle(): void;
  onNavigate(): void;
  /** problem count shown on the collection-status item (from the server, optional) */
  collectionProblems?: number;
}

export function SideRail({ current, narrow, open, tablet, onToggle, onNavigate, collectionProblems }: RailProps) {
  const t = useT();
  const perms = usePerms();
  const items = menuFor(perms);
  const main = items.filter((m) => !m.group);
  const adm = items.filter((m) => m.group === 'admin');
  const expanded = tablet ? open : !narrow;
  const toggleLabel = t(expanded ? 'rail.collapse' : 'rail.expand');
  const toggleRef = useRef<HTMLButtonElement>(null);
  const onKey = (e: ReactKeyboardEvent<HTMLElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    const list = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('.side-rail__item, .rail-help, .side-rail__toggle'));
    const i = list.indexOf(document.activeElement as HTMLElement);
    if (i < 0) return;
    e.preventDefault();
    const n = e.key === 'Home' ? 0 : e.key === 'End' ? list.length - 1 : (i + (e.key === 'ArrowDown' ? 1 : -1) + list.length) % list.length;
    list[n]?.focus();
  };
  const item = (m: MenuItem, i: number) => {
    const label = t(m.key);
    const badge = m.id === 'admin/collection' && collectionProblems ? (
      <em className="side-rail__badge" aria-label={t('rail.problems', { n: collectionProblems })}>
        {collectionProblems}
      </em>
    ) : null;
    return (
      <li key={m.id}>
        <Link
          className="side-rail__item"
          to={'/' + m.path}
          data-route={m.path}
          style={{ ['--i' as string]: i }}
          data-tip={label}
          data-tip-when="rail-narrow"
          data-tip-side="right"
          aria-current={current === m.id ? 'page' : undefined}
          onClick={() => {
            if (tablet) onNavigate();
          }}
        >
          <Icon name={m.icon} />
          <span className="side-rail__label">{label}</span>
          {badge}
        </Link>
      </li>
    );
  };
  return (
    <nav className="side-rail" id="app-rail" aria-label={t('nav.main')} onKeyDown={onKey}>
      <div className="side-rail__scroll">
        <Link className="side-rail__brand" to="/home" aria-label={`${t('app.name')} · ${t('nav.home')}`}>
          <span className="side-rail__mark" aria-hidden="true" />
          <span className="side-rail__name">
            {t('app.name')}
            <small>{t('app.tagline')}</small>
          </span>
        </Link>
        <ul className="side-rail__nav">{main.map(item)}</ul>
        {adm.length ? (
          <>
            <p className="side-rail__group" id="rail-admin">
              {t('nav.admin')}
            </p>
            <ul className="side-rail__nav" aria-labelledby="rail-admin">
              {adm.map((m, i) => item(m, main.length + i))}
            </ul>
          </>
        ) : null}
      </div>
      <div className="side-rail__foot">
        <Link
          className="rail-help"
          to="/glossary?cat=usage"
          aria-label={`${t('rail.help.title')} · ${t('rail.help.link')}`}
          data-tip={t('rail.help.title')}
          data-tip-when="rail-narrow"
          data-tip-side="right"
          onClick={() => {
            if (tablet) onNavigate();
          }}
        >
          <span className="rail-help__ic">
            <Icon name="help" />
          </span>
          <span className="rail-help__text">
            <b>{t('rail.help.title')}</b>
            <span>{t('rail.help.body')}</span>
            <em>{t('rail.help.link')} →</em>
          </span>
        </Link>
        <button
          ref={toggleRef}
          className="side-rail__toggle"
          type="button"
          aria-expanded={expanded}
          aria-controls="app-rail"
          aria-label={toggleLabel}
          data-tip={toggleLabel}
          data-tip-side="right"
          onClick={() => {
            onToggle();
            // the button moves while the rail width changes (.45 s): re-place its tooltip afterwards
            setTimeout(() => refreshTip(toggleRef.current), 520);
          }}
        >
          <Icon name="chevL" />
        </button>
      </div>
    </nav>
  );
}

/** Phone bottom tabs: 4 tabs + "더보기" (nav with aria-current, not a tab list). */
export function BottomTabs({ current }: { current: MenuId | null }) {
  const t = useT();
  const perms = usePerms();
  const [more, setMore] = useState(false);
  const all = menuFor(perms);
  const tabIds = bottomTabsFor(perms);
  const tabs = tabIds.map((id) => all.find((m) => m.id === id)).filter((m): m is MenuItem => !!m);
  const rest = all.filter((m) => !tabIds.includes(m.id));
  const inMore = !!current && !tabIds.includes(current);
  const curItem = current ? menuItem(current) : undefined;
  return (
    <>
      <nav className="bottom-tabs" aria-label={t('nav.bottom')}>
        {tabs.map((m) => (
          <Link key={m.id} className="bottom-tabs__item" to={'/' + m.path} data-route={m.path} aria-current={current === m.id ? 'page' : undefined}>
            <Icon name={m.icon} />
            <span>{t(m.key)}</span>
          </Link>
        ))}
        <button className={cls('bottom-tabs__item bottom-tabs__more', inMore && 'is-current')} type="button" aria-haspopup="dialog" aria-describedby="more-cur" onClick={() => setMore(true)}>
          <Icon name="grid" />
          <span>{t('nav.more')}</span>
          <span className="visually-hidden" id="more-cur">
            {inMore && curItem ? t('nav.moreCurrent', { page: t(curItem.key) }) : ''}
          </span>
        </button>
      </nav>
      <Modal open={more} onClose={() => setMore(false)} title={t('nav.moreTitle')} className="sheet--menu">
        <ul className="more-list">
          {rest.map((m) => (
            <li key={m.id}>
              <Link className="more-list__item" to={'/' + m.path} aria-current={current === m.id ? 'page' : undefined} onClick={() => setMore(false)}>
                <span className="more-list__ic">
                  <Icon name={m.icon} />
                </span>
                <span>
                  <b>{t(m.key)}</b>
                  <small>{t(m.key + '.desc')}</small>
                </span>
                <Icon name="chevR" className="more-list__chev" />
              </Link>
            </li>
          ))}
        </ul>
      </Modal>
    </>
  );
}
